import type { StagedPhotoMeta } from '../../../utils/bulkImportClustering';
import type { CarouselEvent } from '../../../types';
import type { ClusterState } from './types';

interface UnsortedTrayProps {
  photos: StagedPhotoMeta[];
  clusters: ClusterState[];
  existingEvents: CarouselEvent[];
  onAssign: (clientId: string, targetClusterId: string) => void;
  onAssignToExistingEvent: (clientId: string, eventId: string) => void;
  onDiscard: (clientId: string) => void;
}

export function UnsortedTray({ photos, clusters, existingEvents, onAssign, onAssignToExistingEvent, onDiscard }: UnsortedTrayProps) {
  return (
    <div className="mt-6 rounded-xl border border-dashed border-amber-200 bg-amber-50/60 p-4">
      <p className="text-sm font-medium text-amber-800 mb-1">
        {photos.length} photo{photos.length === 1 ? '' : 's'} have no capture date
      </p>
      <p className="text-xs text-amber-700 mb-3">
        We can't tell when these were taken, so they weren't auto-grouped. Assign them to an event below, or leave them out — nothing here gets uploaded automatically.
      </p>
      <div className="space-y-2">
        {photos.map(photo => (
          <div key={photo.clientId} className="flex items-center gap-3 bg-white rounded-lg p-2 border border-amber-100">
            <img src={photo.thumbDataUrl} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
            <select
              defaultValue=""
              onChange={e => {
                const value = e.target.value;
                if (!value) return;
                if (value.startsWith('cluster:')) onAssign(photo.clientId, value.slice('cluster:'.length));
                else if (value.startsWith('event:')) onAssignToExistingEvent(photo.clientId, value.slice('event:'.length));
              }}
              className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="" disabled>Assign to event…</option>
              {clusters.length > 0 && (
                <optgroup label="New events found in this import">
                  {clusters.map(c => (
                    <option key={c.id} value={`cluster:${c.id}`}>{c.title}</option>
                  ))}
                </optgroup>
              )}
              {existingEvents.length > 0 && (
                <optgroup label="Existing events">
                  {existingEvents.map(e => (
                    <option key={e.id} value={`event:${e.id}`}>{e.title}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              type="button"
              onClick={() => onDiscard(photo.clientId)}
              className="text-xs text-slate-400 hover:text-red-600 flex-shrink-0"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
